import { Directive, inject, OnDestroy, OutputRefSubscription } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Scan3dLengthInputComponent } from './scan-3d-length-input.component';

@UntilDestroy()
@Directive({
  selector:
    // eslint-disable-next-line @angular-eslint/directive-selector
    'sd-scan-3d-length-input[formControl], sd-scan-3d-length-input[formControlName], sd-scan-3d-length-input[ngModel]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: Scan3dLengthInputDirective,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: Scan3dLengthInputDirective,
      multi: true,
    },
  ],
})
export class Scan3dLengthInputDirective implements ControlValueAccessor, Validator, OnDestroy {
  private readonly host = inject(Scan3dLengthInputComponent);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value?: number) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onValidatorChange: () => void = () => {};

  private lengthChangedRef: OutputRefSubscription;
  private validationChangedRef: OutputRefSubscription;

  constructor() {
    this.lengthChangedRef = this.host.lengthChanged.subscribe((length?: number) => {
      this.onChange(length);
      this.onTouched();
    });

    this.validationChangedRef = this.host.validationChanged.subscribe(() => {
      this.onValidatorChange();
    });

    this.host.form.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.host.form.dirty) this.onTouched();
    });
  }

  ngOnDestroy(): void {
    this.lengthChangedRef.unsubscribe();
    this.validationChangedRef.unsubscribe();
  }

  writeValue(value: number): void {
    this.host.setInternalLengthValue(value);
  }

  registerOnChange(fn: (value?: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.host.setDisabledState(isDisabled);
  }

  validate(): ValidationErrors | null {
    return this.host.getValidationErrors();
  }
}
